<template>
  <div>
    <v-container class="px-6 mt-2" fluid>
      <v-row>
        <v-col cols="12">
          <h1 class="text-h4 font-weight-bold">Leaderboard</h1>
          <h2 class="text-subtitle-1 mb-0">
            See how you and others are doing on the leaderboard
          </h2>
        </v-col>
      </v-row>
    </v-container>
    <v-container>
      <v-row v-if="!loading">
        <v-col cols="12">
          <div class="text-center">
            <h3 class="text-h5 mt-6">
              Photographers' board
            </h3>
            <p>
              Here is where we reward our community
            </p>
          </div>
        </v-col>
        <v-col cols="12" md="4">
          <User-card-list
            :data="mostActiveUsers"
            :loading="loadingMore"
            :load-more="loadMoreMostActiveUsers"
            title="Most active users"
            subtitle="Updates weekly"
          />
        </v-col>
        <v-col cols="12" md="4">
          <User-card-list
            :data="mostActiveCritique"
            :loading="loadingMore"
            :load-more="loadMoreMostActiveCritique"
            title="Most active commenters"
            subtitle="Updates weekly"
          />
        </v-col>
        <v-col cols="12" md="4">
          <User-card-list
            :data="mostPublishedPhotos"
            :loading="loadingMore"
            :load-more="loadMoreMostPublishedPhotos"
            title="Most published photographers"
            subtitle="Updates weekly"
          />
        </v-col>
      </v-row>
    </v-container>
    <v-container fluid>
      <v-row v-if="!loading">
        <v-col cols="12">
          <div class="text-center">
            <h3 class="text-h5 mt-12">
              Most rated photos
            </h3>
            <p>
              100ASA favourite photos by the community
            </p>
          </div>
          <v-tabs centered background-color="transparent">
            <v-tab v-for="(tab, i) in tabs" :key="i" :href="`#tab-${i}`">
              {{ tab }}
            </v-tab>
            <!-- Best photos of the day -->
            <v-tab-item value="tab-0">
              <v-row align="center">
                <v-col
                  v-for="(photo, index) in bestPhotosOfTheDay"
                  :key="index"
                  cols="12"
                  sm="6"
                  lg="4"
                  xl="3"
                >
                  <PhotoDetails :photo="photo" :me="me" />
                </v-col>
              </v-row>
            </v-tab-item>
            <!-- End best photos of the day -->
            <!-- Best photos of the week -->
            <v-tab-item value="tab-1">
              <v-row align="center">
                <v-col
                  v-for="(photo, index) in bestPhotosOfTheWeek"
                  :key="index"
                  cols="12"
                  sm="6"
                  lg="4"
                  xl="3"
                >
                  <PhotoDetails :photo="photo" :me="me" />
                </v-col>
              </v-row>
            </v-tab-item>
            <!-- End best photos of the week -->
            <!-- Best photos of the month -->
            <v-tab-item value="tab-2">
              <v-row align="center">
                <v-col
                  v-for="(photo, index) in bestPhotosOfTheMonth"
                  :key="index"
                  cols="12"
                  sm="6"
                  lg="4"
                  xl="3"
                >
                  <PhotoDetails :photo="photo" :me="me" />
                </v-col>
              </v-row>
            </v-tab-item>
            <!-- End Best photos of the month -->
          </v-tabs>
        </v-col>
      </v-row>
    </v-container>
    <div v-if="loading" class="px-3 text-center">
      <v-icon class="fa-spin">
        fal fa-spinner
      </v-icon>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import UserCardList from '@/components/leaderboard/UserCardList.vue'
import PhotoDetails from '@/components/leaderboard/PhotoDetails.vue'
import types from '@/utils/types'

export default {
  components: {
    UserCardList,
    PhotoDetails
  },
  middleware: 'header',
  data() {
    return {
      types,
      loading: false,
      loadingMore: false,
      tabs: ['Day', 'Week', 'Month']
    }
  },
  head({ $seo }) {
    return $seo({
      title: 'Leaderboard',
      description: "Check what 100ASA's photographers are achieving in our community. Most active. Most comments. Most published."
    })
  },
  computed: {
    ...mapState({
      me: state => state.auth.user,
      bestPhotosOfTheDay: state => state.leaderboard.bestPhotosOfTheDay,
      bestPhotosOfTheWeek: state => state.leaderboard.bestPhotosOfTheWeek,
      bestPhotosOfTheMonth: state => state.leaderboard.bestPhotosOfTheMonth,
      mostActiveUsers: state => state.leaderboard.mostActiveUsers,
      mostActiveUsersIsLastPage: state =>
        state.leaderboard.mostActiveUsersIsLastPage,
      mostActiveUsersPage: state => state.leaderboard.mostActiveUsersPage,
      mostActiveCritique: state => state.leaderboard.mostActiveCritique,
      mostActiveCritiqueIsLastPage: state =>
        state.leaderboard.mostActiveCritiqueIsLastPage,
      mostActiveCritiquePage: state => state.leaderboard.mostActiveCritiquePage,
      mostPublishedPhotos: state => state.leaderboard.mostPublishedPhotos,
      mostPublishedPhotosIsLastPage: state =>
        state.leaderboard.mostPublishedPhotosIsLastPage,
      mostPublishedPhotosPage: state =>
        state.leaderboard.mostPublishedPhotosPage
    })
  },
  async mounted() {
    if (this.bestPhotosOfTheDay.length === 0) {
      const initialData = {
        page: 1
      }
      try {
        this.loading = true
        await this.$store.dispatch('leaderboard/getBestPhotos')
        await this.$store.dispatch(
          'leaderboard/getMostActiveUsers',
          initialData
        )
        await this.$store.dispatch(
          'leaderboard/getMostActiveCritique',
          initialData
        )
        await this.$store.dispatch(
          'leaderboard/getMostPublishedPhotos',
          initialData
        )
      } catch (error) {
        this.$asa.appInsights('Pages: leaderboard.vue - method: mounted', error)
      } finally {
        this.loading = false
      }
    }
  },
  methods: {
    async loadMoreMostActiveUsers() {
      if (
        !this.loadingMore &&
        !this.mostActiveUsersIsLastPage &&
        this.mostActiveUsers.length > 0
      ) {
        try {
          this.loadingMore = true
          await this.$store.dispatch('leaderboard/getMostActiveUsers', {
            page: this.mostActiveUsersPage + 1
          })
        } catch (error) {
          this.$asa.appInsights('Pages: leaderboard.vue - method: loadMoreMostActiveUsers', error)
        } finally {
          this.loadingMore = false
        }
      }
    },
    async loadMoreMostActiveCritique() {
      if (
        !this.loadingMore &&
        !this.mostActiveCritiqueIsLastPage &&
        this.mostActiveCritique.length > 0
      ) {
        try {
          this.loadingMore = true
          await this.$store.dispatch('leaderboard/getMostActiveCritique', {
            page: this.mostActiveCritiquePage + 1
          })
        } catch (error) {
          this.$asa.appInsights('Pages: leaderboard.vue - method: loadMoreMostActiveCritique', error)
        } finally {
          this.loadingMore = false
        }
      }
    },
    async loadMoreMostPublishedPhotos() {
      if (
        !this.loadingMore &&
        !this.mostPublishedPhotosIsLastPage &&
        this.mostPublishedPhotos.length > 0
      ) {
        try {
          this.loadingMore = true
          await this.$store.dispatch('leaderboard/getMostPublishedPhotos', {
            page: this.mostPublishedPhotosPage + 1
          })
        } catch (error) {
          this.$asa.appInsights('Pages: leaderboard.vue - method: loadMoreMostPublishedPhotos', error)
        } finally {
          this.loadingMore = false
        }
      }
    }
  }
}
</script>

<style scoped>
.theme--light.v-tabs-items {
  background-color: transparent !important;
}
</style>
